import { z } from "zod";
import { zId, zInstanteUtc } from "./dominio.js";
/**
 * Mural de anotações rápidas — RN-50.
 *
 * **Não é caderno.** A nota do caderno tem título, categoria, itens anexados,
 * detecção de conflito e conteúdo rico; isto é um papel colado na parede: um
 * texto curto, uma cor e um lugar. Juntar as duas faria a nota de recado herdar
 * um editor inteiro e a nota de estudo perder o título.
 *
 * **A paleta é fixa e exclui a faixa do ritual.** Cor de ritual identifica
 * ofensiva, meta e heatmap; repetida num papel de recado ela deixa de
 * identificar qualquer coisa. Quatro cores em croma baixo, nomeadas pela
 * posição e não pelo matiz — o mesmo motivo do vocabulário do caderno: o papel
 * escrito hoje continua válido no dia em que a paleta mudar de tom.
 */
export const CORES_DO_MURAL = ["mural-1", "mural-2", "mural-3", "mural-4"];
export const zCorDoMural = z.enum(CORES_DO_MURAL);
export const zAnotacaoRapida = z.object({
    id: zId,
    texto: z.string(),
    cor: zCorDoMural,
    /**
     * Fração da área do mural, de 0 a 1 — **não pixel**. O mural muda de largura
     * com a janela, e posição em pixel gravada num monitor põe o papel fora da
     * tela no outro.
     */
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
    /** O que vale na tela estreita, onde não há arrasto. */
    ordem: z.number().int(),
    criadoEm: zInstanteUtc,
    atualizadoEm: zInstanteUtc,
});
export const zAnotacaoRapidaEntrada = z.object({
    /**
     * Curto de propósito. Papel de recado que aceita mil caracteres vira caderno
     * mal feito — e o caderno existe, ao lado, para o que é longo.
     */
    texto: z.string().trim().max(500),
    cor: zCorDoMural,
    /**
     * Onde o papel nasce, quando quem cria sabe dizer.
     *
     * Opcional porque só o navegador tem a geometria: a parede muda de largura
     * com a janela, e a fração que o papel ocupa nela só existe depois de medir.
     * Sem isto, o servidor escolhe sozinho com `proximaPosicaoLivre` e o tamanho
     * de referência — é o caminho dos comandos de terminal e do script de
     * capturas, que não têm tela.
     */
    x: z.number().min(0).max(1).optional(),
    y: z.number().min(0).max(1).optional(),
});
/** Só a posição. É o que o arrasto grava, por debounce (RN-50). */
export const zPosicaoNoMural = z.object({
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
});
/** A reordenação explícita da tela estreita: a lista inteira, na ordem nova. */
export const zOrdemDoMural = z.object({ ids: z.array(zId) });
export const zMural = z.object({ anotacoes: z.array(zAnotacaoRapida) });
/**
 * A próxima cor, girando pela paleta.
 *
 * Papel novo sempre da mesma cor faria o mural virar um bloco monocromático, e
 * pedir a cor antes de escrever poria uma decisão de enfeite na frente da ideia
 * que se quer anotar. Girar resolve os dois: cada papel nasce diferente do
 * anterior, e trocar depois continua sendo um toque.
 */
export function proximaCor(quantidade) {
    return CORES_DO_MURAL[quantidade % CORES_DO_MURAL.length];
}
/** Alvo de rota com `:id`. Um por módulo, como nos outros (ADR 005). */
export const zMuralId = z.object({ id: zId });
/* ----------------------------------------------- onde o papel novo nasce */
/**
 * O tamanho do papel como fração da parede, na largura de referência.
 *
 * Derivado dos tokens que desenham as duas coisas: o papel tem
 * `--mural-papel-largura` (12rem) numa parede de `--mural-parede-altura`
 * (22rem) de altura, e a parede ocupa a coluna de conteúdo inteira — cerca de
 * 69rem na largura ampla com a barra lateral aberta. Daí 12/69 e 8/22, com a
 * altura do papel medida em duas linhas de texto mais a fileira de cores.
 *
 * Serve **só de padrão**, para quem cria sem tela: comando de terminal, script
 * de capturas. Quem tem navegador mede a parede e o papel de verdade e manda a
 * posição junto — a parede muda de largura com a janela, e nenhum número aqui
 * acerta todas.
 */
export const TAMANHO_PADRAO_DO_PAPEL = { largura: 0.18, altura: 0.36 };
/**
 * A posição em que um papel novo cabe sem cobrir nenhum outro.
 *
 * **O defeito que ela conserta:** a cascata anterior deslocava cada papel novo
 * em 0,07 da parede, e o papel ocupa cerca de 0,18 dela — três anotações
 * nasciam empilhadas num canto com 80% da parede vazia à direita. Pior, o
 * deslocamento vinha da *quantidade* de papéis, então apagar um fazia o próximo
 * renascer exatamente em cima de outro.
 *
 * Varre uma grade do tamanho do papel, da esquerda para a direita e de cima
 * para baixo, e devolve o primeiro slot que não intersecta ninguém. Parede
 * cheia devolve o slot menos disputado — em algum momento sobrepor é
 * inevitável, e sobrepor de propósito no fim é melhor do que empilhar no começo.
 */
export function proximaPosicaoLivre(ocupadas, tamanho = TAMANHO_PADRAO_DO_PAPEL) {
    const largura = Math.min(Math.max(tamanho.largura, 0.02), 1);
    const altura = Math.min(Math.max(tamanho.altura, 0.02), 1);
    const colunas = Math.max(1, Math.floor(1 / largura));
    const linhas = Math.max(1, Math.floor(1 / altura));
    const cobre = (ax, ay, bx, by) => ax < bx + largura && bx < ax + largura && ay < by + altura && by < ay + altura;
    let menosDisputado = { x: 0, y: 0, quantos: Number.POSITIVE_INFINITY };
    for (let linha = 0; linha < linhas; linha++) {
        for (let coluna = 0; coluna < colunas; coluna++) {
            const x = Math.min(coluna * largura, 1 - largura);
            const y = Math.min(linha * altura, 1 - altura);
            const quantos = ocupadas.filter((o) => cobre(x, y, o.x, o.y)).length;
            if (quantos === 0)
                return { x, y };
            if (quantos < menosDisputado.quantos)
                menosDisputado = { x, y, quantos };
        }
    }
    return { x: menosDisputado.x, y: menosDisputado.y };
}
//# sourceMappingURL=mural.js.map